import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fmtBRL, fmtInt, STATUS_LABELS } from "@/lib/api";
import { ArrowLeft, UserCircle, MapPin, EnvelopeSimple, Phone, ClockClockwise, Package, CurrencyCircleDollar } from "@phosphor-icons/react";
import { useCanAdmin } from "@/lib/useCanAdmin";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canAdmin } = useCanAdmin();
  const [c, setC] = useState(null);

  useEffect(() => {
    api.get(`/customers/${id}`).then(({ data }) => setC(data));
  }, [id]);

  if (!c) return <div className="p-12">Carregando…</div>;

  return (
    <div className="p-6 md:p-10">
      <button onClick={() => navigate("/customers")} className="flex items-center gap-2 text-sm mb-6 hover:underline" data-testid="cust-back">
        <ArrowLeft size={16} /> Voltar para Clientes
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: customer info */}
        <div className="lg:col-span-4">
          <div className="border border-black bg-white p-6 mb-4" data-testid="cust-info">
            <div className="flex items-center gap-3 mb-4">
              <UserCircle size={48} weight="bold" />
              <div>
                <div className="overline text-zinc-500">CLIENTE</div>
                <div className="font-head text-2xl font-black leading-tight">{c.name}</div>
              </div>
            </div>
            {c.document && <div className="font-mono text-sm text-zinc-600 mb-4">{c.document}</div>}
            <div className="space-y-3 text-sm">
              {c.email && <div className="flex items-start gap-2"><EnvelopeSimple size={16} className="mt-0.5 text-zinc-400" /> <span className="break-all">{c.email}</span></div>}
              {(c.cellphone || c.phone) && <div className="flex items-center gap-2"><Phone size={16} className="text-zinc-400" /> <span className="font-mono">{c.cellphone || c.phone}</span></div>}
              {(c.address || c.city) && (
                <div className="flex items-start gap-2 text-zinc-700">
                  <MapPin size={16} className="mt-0.5 text-zinc-400" />
                  <div>
                    {c.address && <>{c.address}{c.address_number && `, ${c.address_number}`}{c.complement && ` — ${c.complement}`}<br /></>}
                    {c.neighborhood && <>{c.neighborhood}<br /></>}
                    {c.city && <>{c.city}/{c.state} · {c.zipcode}</>}
                  </div>
                </div>
              )}
            </div>
            {c.notes && (
              <div className="mt-4 pt-4 border-t">
                <div className="overline text-zinc-500 mb-1">OBSERVAÇÕES</div>
                <div className="text-sm">{c.notes}</div>
              </div>
            )}
            <div className="mt-4 pt-4 border-t text-xs text-zinc-500 space-y-1">
              <div>Fonte: <span className="font-mono uppercase">{c.source || "manual"}</span></div>
              {c.bling_id && <div>Bling ID: <span className="font-mono">{c.bling_id}</span></div>}
              {c.created_at && <div>Cadastrado: <span className="font-mono">{new Date(c.created_at).toLocaleDateString("pt-BR")}</span></div>}
            </div>
          </div>

          {/* Lifetime stats */}
          <div className="grid grid-cols-2 gap-[1px] bg-black border border-black">
            <div className="bg-white p-4">
              <div className="overline text-zinc-500 mb-1 flex items-center gap-1"><Package size={12} /> PEDIDOS</div>
              <div className="font-mono text-3xl font-semibold">{fmtInt(c.orders_count)}</div>
            </div>
            <div className="bg-white p-4">
              <div className="overline text-zinc-500 mb-1 flex items-center gap-1"><CurrencyCircleDollar size={12} /> LTV</div>
              <div className="font-mono text-xl font-semibold text-[#00A300]">{fmtBRL(c.total_lifetime_value)}</div>
            </div>
          </div>
        </div>

        {/* Right: orders history */}
        <div className="lg:col-span-8">
          {c.last_order_at && (
            <div className="border border-black p-5 bg-black text-white mb-4" data-testid="last-order-summary">
              <div className="overline text-white/60 mb-2 flex items-center gap-2"><ClockClockwise size={14} weight="bold" /> ÚLTIMO PEDIDO</div>
              <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                  <div className="font-mono text-3xl font-semibold">#{c.last_order_number}</div>
                  <div className="text-xs text-white/60 mt-1">{new Date(c.last_order_at).toLocaleString("pt-BR")}</div>
                </div>
                <div className="text-right">
                  <div className="overline text-white/60">VALOR LÍQUIDO</div>
                  <div className="font-mono text-2xl font-semibold text-[#00FF7F]">{canAdmin ? fmtBRL(c.last_order_value) : "—"}</div>
                </div>
              </div>
              {c.last_order_items?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/20">
                  <div className="overline text-white/60 mb-2">ITENS DO ÚLTIMO PEDIDO</div>
                  <div className="space-y-1 text-sm">
                    {c.last_order_items.map((it, i) => (
                      <div key={`${it.sku || "item"}-${i}`} className="flex justify-between font-mono">
                        <span>{it.sku} · {it.qty}x {it.name}</span>
                        <span className="text-[#FFC000]">{it.cups} copos</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="border border-black bg-white">
            <div className="border-b border-black p-4 overline text-zinc-500">HISTÓRICO COMPLETO ({fmtInt(c.orders_count)})</div>
            {c.orders.length === 0 ? (
              <div className="p-10 text-center text-zinc-400">Nenhum pedido encontrado.</div>
            ) : (
              <table className="table" data-testid="cust-orders-table">
                <thead><tr><th>Pedido</th><th>Data</th><th>Marketplace</th><th>Status</th><th className="text-center">Copos</th>{canAdmin && <th className="text-right">Líquido</th>}</tr></thead>
                <tbody>
                  {c.orders.map((o) => (
                    <tr key={o.id} className="cursor-pointer" onClick={() => navigate(`/orders/${o.id}`)}>
                      <td className="font-mono font-semibold"><Link className="hover:underline" to={`/orders/${o.id}`}>#{o.bling_number}</Link></td>
                      <td className="font-mono text-xs">{new Date(o.created_at).toLocaleDateString("pt-BR")}</td>
                      <td className="text-zinc-600 text-xs">{o.marketplace}</td>
                      <td><span className="flex items-center gap-2 text-xs"><span className={`dot ${o.production_status}`} />{STATUS_LABELS[o.production_status]}</span></td>
                      <td className="text-center font-mono">{fmtInt(o.total_cups || 0)}</td>
                      {canAdmin && <td className="text-right font-mono font-semibold">{fmtBRL(o.net_value)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
